// CalendarYearDropdown.js
import React, { useEffect, useRef, useState, memo } from "react";

const CalendarYearDropdown = memo(function CalendarYearDropdown({
    year,          // current selected year (number or string)
    years,         // array of years (numbers)
    onChange,      // function(newYear:number)
    nudgePx = 0,   // visual centering nudge
}) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
		const selectedRef = useRef(null);
		const hoverRef = useRef(null);
		const [hoveredYear, setHoveredYear] = useState(null);

    useEffect(() => {
        const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);

		useEffect(() => {
			if (open && selectedRef.current) {
				selectedRef.current.scrollIntoView({
					block: "center",
					behavior: "instant" // or "smooth" if you prefer a nice scroll
				});

				// initialize hover on open for keyboard users
      	setHoveredYear(Number(year));
			}
		}, [open, year]);

		// Also scroll when hovering via keyboard
		useEffect(() => {
			if (open && hoverRef.current) {
				hoverRef.current.scrollIntoView({ block: "nearest", behavior: "instant" });
			}
		}, [hoveredYear, open]);

		const handleKeyDown = (e) => {
			if (!open) return;

			const base = Number(hoveredYear || year);
			const idx = years.indexOf(base);

			if (e.key === "ArrowDown") {
				e.preventDefault();
				const next = Math.min(years.length - 1, idx + 1);  // go DOWN → idx + 1
				setHoveredYear(years[next]);
			} else if (e.key === "ArrowUp") {
				e.preventDefault();
				const prev = Math.max(0, idx - 1);                 // go UP → idx - 1
				setHoveredYear(years[prev]);
			} else if (e.key === "Enter") {
				e.preventDefault();
				if (hoveredYear != null) {
					onChange(Number(hoveredYear));
					setOpen(false);
				}
			} else if (e.key === "Escape") {
				setOpen(false);
			} else if (e.key === "Home") {
				e.preventDefault();
				setHoveredYear(years[0]);                           // jump to first (top)
			} else if (e.key === "End") {
				e.preventDefault();
				setHoveredYear(years[years.length - 1]);            // jump to last (bottom)
			}
		};

    return (
        <div
            ref={ref}
            className="react-datepicker__header__dropdown react-datepicker__header__dropdown--select"
            style={{ position: "relative", display: "inline-flex", alignItems: "center" }}
        >
            <button
                type="button"
                onClick={() => setOpen(o => !o)}
								onKeyDown={handleKeyDown} 
                className="react-datepicker__year-select"
                style={{
                    appearance: "none",
                    background: "transparent",
                    border: "none",
                    fontWeight: 400,
                    fontSize: "0.85rem",
                    lineHeight: "1.2",
                    color: "#000",
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    marginLeft: nudgePx,   // ← visual centering nudge
                    textAlign: "center",
                }}
                aria-haspopup="listbox"
                aria-expanded={open}
            >
                {year}
                <span style={{ fontSize: "0.8em" }}>▾</span>
            </button>

            {open && (
                <div
                    role="listbox"
										tabIndex={-1}
                    style={{
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        marginTop: 4,
                        maxHeight: 300,        // fixed height
                        overflowY: "auto",
                        background: "#fff",
                        border: "1px solid #dcdcdc",
                        borderRadius: 4,
                        boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                        zIndex: 5,
                        minWidth: 80,
                    }}
										onKeyDown={handleKeyDown}
                >
                    
                    {years.map((y) => {
											const isSelected = String(y) === String(year);
											const isHovered = Number(hoveredYear) === Number(y);

											return (
												<div
														key={y}
														ref={isSelected ? selectedRef : isHovered ? hoverRef : null}
														role="option"
														aria-selected={isSelected}
														onMouseDown={(e) => e.preventDefault()}
														onMouseEnter={() => setHoveredYear(y)}
														onClick={() => { onChange(Number(y)); setOpen(false); }}
														style={{
																padding: "6px 10px",
																textAlign: "center",
																cursor: "pointer",
																background: isSelected 
																	? "#007bff" 
																	: isHovered
																		? "#718afa1a"
																		: "transparent",
																color: isSelected ? "#fff" : "#000",
														}}
												>
														{y}
												</div>
											);
										})}
                </div>
            )}
        </div>
    );
});

export default CalendarYearDropdown;



// WEBPACK FOOTER //
// ./src/components/CalendarYearDropdown.js